/**
 * MY OFFERINGS (sponsor lifecycle). Every offering across every asset, in one place.
 *
 * A sponsor issues over time, like a company issuing shares in rounds, so they need the whole book at a
 * glance: what is live and raising, what is drafted, what has closed, and how far each has sold. Each
 * row links back to the asset it belongs to.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type SponsorOffering } from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDate } from '@/lib/format';

const num = (n: number) => n.toLocaleString('en-KE');

export default function MyOfferings() {
  const [offerings, setOfferings] = useState<SponsorOffering[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setOfferings((await api.sponsor.offerings()).offerings);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your offerings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!offerings) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link
        to="/tokenize"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Tokenize
      </Link>

      <PageHeader title="My offerings" subtitle="Every fundraising round across your assets." />

      {offerings.length === 0 ? (
        <EmptyState
          title="No offerings yet"
          body="Once an asset is issued, bring part of your allocation to market as an offering from its page."
          action={{ label: 'Go to my assets', href: '/tokenize' }}
        />
      ) : (
        <ul className="space-y-3">
          {offerings.map((o) => (
            <li key={o.id}>
              <Reveal>
                <Link
                  to={`/tokenize/assets/${o.assetId}`}
                  className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{o.asset}</p>
                      <p className="tabular text-micro text-ink-subtle">
                        {o.name} · {num(o.tokens)} tokens at KES {o.price}
                      </p>
                    </div>
                    <StatusPill status={o.status} />
                  </div>
                  {o.status === 'live' ? (
                    <div className="mt-3">
                      <div className="h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${Math.round((o.sold / o.tokens) * 100)}%` }}
                        />
                      </div>
                      <p className="tabular mt-1 text-nano text-ink-subtle">
                        {num(o.sold)} of {num(o.tokens)} sold · closes {fmtDate(o.closes)}
                      </p>
                    </div>
                  ) : (
                    <p className="tabular mt-2 text-nano text-ink-subtle">
                      Opens {fmtDate(o.opens)} · closes {fmtDate(o.closes)}
                    </p>
                  )}
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
